import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft,
  ChevronDown,
  Home,
  Briefcase,
  Bookmark,
  Plus,
  MapPin,
  Trash2,
  Search,
  Navigation,
  Mail,
  ChevronRight,
} from 'lucide-react';
import { useAuthStore, SavedPlace } from '../../stores/authStore';
import { useMapStore } from '../../stores/mapStore';

interface SavedPlacesPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigateBack?: () => void;
  onAddPlace?: (label: 'home' | 'work' | 'saved') => void;
  onFindPostcode?: () => void;
}

export function SavedPlacesPanel({ isOpen, onClose, onNavigateBack, onAddPlace, onFindPostcode }: SavedPlacesPanelProps) {
  const { savedPlaces, removeSavedPlace } = useAuthStore();
  const { setSelectedPlaceFromData } = useMapStore();
  const [placesFilter, setPlacesFilter] = useState('');
  const [labelFilter, setLabelFilter] = useState<'all' | 'home' | 'work' | 'saved'>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const filteredPlaces = useMemo(() => {
    let places = savedPlaces;
    if (labelFilter !== 'all') {
      places = places.filter((p) => p.label === labelFilter);
    }
    if (placesFilter.trim()) {
      const q = placesFilter.toLowerCase();
      places = places.filter(
        (p) => p.name.toLowerCase().includes(q) || p.address.toLowerCase().includes(q)
      );
    }
    return places;
  }, [savedPlaces, labelFilter, placesFilter]);

  const handlePlaceClick = (place: SavedPlace) => {
    setSelectedPlaceFromData({
      id: place.placeId || `saved-${place.id}`,
      name: place.name,
      category: 'place',
      address: place.address,
      coordinates: place.coordinates,
    });
    onClose();
  };

  const getLabelIcon = (label: SavedPlace['label']) => {
    switch (label) {
      case 'home':
        return <Home size={18} />;
      case 'work':
        return <Briefcase size={18} />;
      default:
        return <Bookmark size={18} />;
    }
  };

  const getLabelColor = (label: SavedPlace['label']) => {
    switch (label) {
      case 'home':
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400';
      case 'work':
        return 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400';
      default:
        return 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400';
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
        className="fixed inset-0 bg-white dark:bg-gray-900 z-[90] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-neutral-700 pt-safe">
          <button
            type="button"
            onClick={onNavigateBack ?? onClose}
            className="p-2 -ml-2 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-800 transition-colors"
          >
            <ChevronLeft size={22} className="text-gray-700 dark:text-neutral-300" />
          </button>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-neutral-100">
            Saved Places
          </h2>
          <button
            type="button"
            onClick={() => onAddPlace?.('saved')}
            className="p-2 -mr-2 rounded-lg text-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
          >
            <Plus size={22} />
          </button>
        </div>

        {onFindPostcode && (
          <div className="px-4 py-3 border-b border-gray-100 dark:border-neutral-800">
            <button
              type="button"
              onClick={onFindPostcode}
              className="w-full flex items-center gap-3 p-3 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors"
            >
              <Mail size={18} className="text-emerald-600 dark:text-emerald-400 shrink-0" />
              <div className="flex-1 text-left">
                <span className="block text-sm font-medium text-emerald-800 dark:text-emerald-200">Find My Postcode</span>
                <span className="block text-xs text-emerald-700/80 dark:text-emerald-300/80">Pin drop or current location</span>
              </div>
              <ChevronRight size={16} className="text-emerald-500 shrink-0" />
            </button>
          </div>
        )}

        {/* Filter tabs */}
        <div className="flex gap-1 px-4 py-2 border-b border-gray-100 dark:border-neutral-800">
          {(
            [
              { value: 'all', label: 'All' },
              { value: 'home', label: 'Home' },
              { value: 'work', label: 'Work' },
              { value: 'saved', label: 'Saved' },
            ] as const
          ).map((tab) => (
            <button
              key={tab.value}
              type="button"
              onClick={() => setLabelFilter(tab.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${labelFilter === tab.value
                  ? 'bg-primary-500 text-white'
                  : 'bg-gray-100 dark:bg-neutral-800 text-gray-600 dark:text-neutral-400 hover:bg-gray-200 dark:hover:bg-neutral-700'
                }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Search */}
        {savedPlaces.length >= 5 && (
          <div className="px-4 py-2 border-b border-gray-100 dark:border-neutral-800">
            <div className="relative">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500"
              />
              <input
                type="text"
                value={placesFilter}
                onChange={(e) => setPlacesFilter(e.target.value)}
                placeholder="Filter saved places..."
                className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-transparent text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-primary-400"
              />
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {savedPlaces.length === 0 ? (
            <div className="text-center py-20 px-4">
              <MapPin size={40} className="mx-auto text-gray-300 dark:text-neutral-600 mb-3" />
              <p className="text-gray-500 dark:text-neutral-400 text-sm">No saved places yet</p>
              <p className="text-gray-400 dark:text-neutral-500 text-xs mt-1">
                Save your home, work, or favorite spots
              </p>
              <button
                type="button"
                onClick={() => onAddPlace?.('saved')}
                className="mt-4 px-4 py-2 bg-primary-500 text-white text-sm rounded-lg font-medium hover:bg-primary-600 transition-colors"
              >
                Add a Place
              </button>
            </div>
          ) : filteredPlaces.length === 0 ? (
            <div className="text-center py-20 px-4">
              <p className="text-gray-400 dark:text-neutral-500 text-sm">
                No places match your filter
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-neutral-800">
              {filteredPlaces.map((place) => {
                const isExpanded = expandedId === place.id;

                return (
                  <div key={place.id}>
                    {/* Summary row */}
                    <button
                      type="button"
                      onClick={() => setExpandedId(isExpanded ? null : place.id)}
                      className={`w-full text-left px-4 py-3 transition-colors ${isExpanded
                          ? 'bg-gray-50 dark:bg-neutral-800/50'
                          : 'hover:bg-gray-50 dark:hover:bg-neutral-800/50'
                        }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${getLabelColor(
                            place.label
                          )}`}
                        >
                          {getLabelIcon(place.label)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 dark:text-white text-sm truncate">
                            {place.name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {place.address}
                          </p>
                        </div>
                        <ChevronDown
                          size={16}
                          className={`text-gray-400 dark:text-neutral-500 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''
                            }`}
                        />
                      </div>
                    </button>

                    {/* Expanded actions */}
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="pb-3 border-l-2 border-purple-200 dark:border-purple-800 ml-[23px] pl-[37px] pr-4">
                            <div className="flex items-center gap-2 pt-1">
                              <button
                                type="button"
                                onClick={() => handlePlaceClick(place)}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                              >
                                <Navigation size={12} />
                                Navigate
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  removeSavedPlace(place.id);
                                  setExpandedId(null);
                                }}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                              >
                                <Trash2 size={12} />
                                Remove
                              </button>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
